import React, { FC, useState, useRef, useEffect } from 'react';
import { AnimatePresence } from 'motion/react';
import { Container } from 'styled/layouts/container';
import { useRouter } from 'next/router';

import {
  NavigationItem,
  NavigationProps,
} from 'components/home/TopNavigation/top-navigation.types';
import SeedLogo from 'components/global/Logo';
import {
  TopNav,
  SiteHeaderNav,
  GlassContainer,
  NavContainer,
  NavDropdown,
  listVariants,
  TabButtonGroup,
  TabButton,
  TabPill,
} from 'components/home/TopNavigation/top-navigation.styled';
import NavSubLists from 'components/home/TopNavigation/nav-sub-lists';
import RightNav from 'components/home/TopNavigation/LargeNavigation/right-nav';
import { trackEvent } from 'lib/analytics';

const LargeNavigation: FC<NavigationProps> = ({
  mainNavigation,
  theme = 'white',
}) => {
  const router = useRouter();
  const [currentTab, setCurrentTab] = useState<NavigationItem>();
  const [selectedTab, setSelectedTab] = useState('');
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const lastFocusedTab = useRef<HTMLButtonElement | null>(null);
  const firstNavSubItemRef = useRef<HTMLAnchorElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTab = () => {
    setSelectedTab('');
    setCurrentTab(undefined);
  };

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      resetTab();
    };
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  const getCurrentTab = (id: string) => {
    return mainNavigation.find((item) => item.id === id);
  };

  const handleMouseEnter = (id: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setSelectedTab(id);
    const tab = getCurrentTab(id);
    setCurrentTab(tab);
    trackEvent('Nav Menu Hover (desktop)', {
      menu: tab?.label,
    });
  };

  const handleMenuEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setCurrentTab(undefined);
      setSelectedTab('');
    }, 300);
  };

  const handleEscape = () => {
    setSelectedTab('');
    setCurrentTab(undefined);
    requestAnimationFrame(() => {
      lastFocusedTab.current?.focus();
    });
  };

  const handleMenuToggle = (id: string) => {
    const isOpen = selectedTab === id;
    if (isOpen) {
      handleEscape();
    } else {
      const tab = getCurrentTab(id);
      setSelectedTab(id);
      setCurrentTab(tab);
      focusFirstNavItem();
    }
  };

  const handleKeyDownTabButton = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    id: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleMenuToggle(id);
    } else if (event.key === 'Escape') {
      handleEscape();
    }
  };

  const handleDropdownKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === 'Escape') {
      handleEscape();
    }
  };

  const focusFirstNavItem = () => {
    setTimeout(() => firstNavSubItemRef.current?.focus(), 0);
  };

  return (
    <Container id='site-header-nav'>
      <TopNav aria-label='Main'>
        <SiteHeaderNav>
          <NavContainer className={`theme-${theme}`}>
            <SeedLogo
              light={theme === 'white'}
              size='natural'
              fontSize='20px'
            />
            {router.pathname !== '/checkout' && (
              <>
                <AnimatePresence key='items' mode='wait'>
                  <TabButtonGroup>
                    {mainNavigation.map((item) => (
                      <TabButton
                        ref={(el) => {
                          tabRefs.current[item.id] = el;
                        }}
                        id={`tab-${item.id}`}
                        aria-controls={`panel-${item.id}`}
                        aria-expanded={selectedTab === item.id}
                        className={`theme-${theme} ${selectedTab === item.id ? 'is-active' : ''}`}
                        key={item.id}
                        type='button'
                        onMouseEnter={() => handleMouseEnter(item.id)}
                        onMouseLeave={() => handleMouseLeave()}
                        onKeyDown={(event) =>
                          handleKeyDownTabButton(event, item.id)
                        }
                        onFocus={() => {
                          lastFocusedTab.current =
                            tabRefs.current[item.id] || null;
                        }}
                      >
                        {item.label}
                        {selectedTab === item.id ? (
                          <TabPill
                            layoutId='tabPill'
                            id='tabPill'
                            transition={{
                              type: 'spring',
                              visualDuration: 0.2,
                              bounce: 0.2,
                            }}
                          />
                        ) : null}
                      </TabButton>
                    ))}
                  </TabButtonGroup>
                </AnimatePresence>
                <GlassContainer />
              </>
            )}
          </NavContainer>

          {router.pathname !== '/checkout' && (
            <AnimatePresence key='tablist' mode='wait'>
              {currentTab && (
                <NavDropdown
                  id={`panel-${currentTab.id}`}
                  aria-labelledby={`tab-${currentTab.id}`}
                  tabIndex={-1}
                  variants={listVariants}
                  initial='initial'
                  animate='visible'
                  exit='exit'
                  onMouseEnter={() => handleMenuEnter()}
                  onMouseLeave={() => handleMouseLeave()}
                  onKeyDown={handleDropdownKeyDown}
                  key={currentTab.id}
                >
                  <AnimatePresence key={currentTab.id} mode='wait'>
                    <NavSubLists
                      currentTab={currentTab}
                      firstNavSubItemRef={firstNavSubItemRef}
                    />
                  </AnimatePresence>
                </NavDropdown>
              )}
            </AnimatePresence>
          )}
        </SiteHeaderNav>
        {router.pathname !== '/checkout' && <RightNav theme={theme} />}
      </TopNav>
    </Container>
  );
};

export default LargeNavigation;
